import { FC, useEffect, useState } from 'react';
import Documents from '@/components/ui/icons/new/Documents';
import ManualSolicitationSummary, {
	FormData,
} from '../ManualSolicitationSummary/ManualSolicitationSummary';
import { useAppDispatch } from '@/lib/hooks/hooks';
import { ErrorHandle } from '@/lib/functions/funcUtils';
import SecondaryButton from '@/components/ui/shared/Button/SecondaryButton/SecondaryButton';
import PrimaryButton from '@/components/ui/shared/Button/PrimaryButton/PrimaryButton';
import {
	ManualSolicitation,
	ManualSolicitationPayload,
} from '@/lib/types/apiTypes';
import { cloneDeep } from 'lodash';

interface ManualSolicitationDrawerProps {
	manualSolicitation: ManualSolicitation | null;
	onChange: (manualSolicitation: ManualSolicitationPayload) => void;
}

const ManualSolicitationDrawer: FC<ManualSolicitationDrawerProps> = ({
	manualSolicitation,
	onChange,
}) => {
	const dispatch = useAppDispatch();
	const [showView, setShowView] = useState(true);
	const [solicitationSummary, setSolicitationSummary] =
		useState<ManualSolicitationPayload | null>(manualSolicitation);

	const formatPayload = (formData: FormData): ManualSolicitationPayload => {
		return {
			name: formData.title,
			type: 'rfpDocument',
			isSolicited: false,
			metaData: {
				funding: formData.fundingAmount || null,
				deadline: formData.deadline || null,
				agency: formData.agency || null,
			},
		};
	};

	const onSubmit = async (formData: FormData) => {
		try {
			const solicitation = formatPayload(formData);
			onChange(solicitation);
			clearChange();
			setShowView(true);
		} catch (error: unknown) {
			ErrorHandle(dispatch, error);
		}
	};

	const clearChange = () => {
		setSolicitationSummary(cloneDeep(manualSolicitation));
	};

	const cancelChange = () => {
		setShowView(true);
		clearChange();
	};

	const toggleView = () => {
		if (!showView) {
			clearChange();
		}
		setShowView((showView) => !showView);
	};

	const controlLayout = () => {
		return (
			<>
				{!showView && (
					<div className="flex justify-center gap-6 mt-auto">
						<PrimaryButton className="w-full self-center" type="submit">
							Update
						</PrimaryButton>
						<SecondaryButton onClick={cancelChange} type="reset">
							Cancel
						</SecondaryButton>
					</div>
				)}
			</>
		);
	};

	useEffect(() => {
		if (manualSolicitation) {
			setSolicitationSummary(cloneDeep(manualSolicitation));
		}
	}, [manualSolicitation]);

	return (
		<div className="flex flex-col h-full w-full">
			<div className="flex justify-between px-6 py-4 bg-white relative shadow-main">
				<div className="flex gap-2">
					<Documents className="h-6" fill={'var(--black)'} />
					<h2 className="text-black self-center font-bold dynamic-medium">
						Project Details
					</h2>
				</div>
				<a
					className="text-primary self-center font-bold dynamic-medium cursor-pointer"
					onClick={toggleView}
					type="submit"
				>
					{showView ? 'Edit Details' : 'Cancel'}
				</a>
			</div>
			<div className="flex flex-col h-full flex-1 bg-lightBackground px-6 py-4 overflow-y-auto hidden-scroll">
				<ManualSolicitationSummary
					type="drawer"
					controlLayout={controlLayout()}
					onSubmit={onSubmit}
					showView={showView}
					solicitationSummary={solicitationSummary}
				/>
			</div>
		</div>
	);
};

export default ManualSolicitationDrawer;
